import React, { useEffect, useState } from 'react';

import { CloseIcon } from '../../../assets';
import {
  connectMetaMessenger,
  getMetaMessengerAccounts,
  getMetaMessengerIntegration,
  updateMetaMessengerSettings,
} from '../../../core/requests/requests';
import {
  MetaAccountProps,
  MetaMessengerIntegrationProps,
} from '../../../core/types/_models';

interface MetaMessengerModalProps {
  onClose: () => void;
  onSaved: (message: string) => void;
}

/**
 * Meta Messenger settings: pick the Facebook page (and optionally the Instagram
 * business account linked to it), then set what the AI should know and how many
 * replies it may send in a single conversation. Everything here can be changed
 * later - it is not part of campaign creation.
 */
const MetaMessengerModal: React.FC<MetaMessengerModalProps> = ({
  onClose,
  onSaved,
}) => {
  const [pages, setPages] = useState<MetaAccountProps[]>([]);
  const [instagramAccounts, setInstagramAccounts] = useState<
    MetaAccountProps[]
  >([]);
  const [integration, setIntegration] =
    useState<MetaMessengerIntegrationProps | null>(null);

  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedInstagramId, setSelectedInstagramId] = useState<string | null>(
    null
  );
  const [backgroundInfo, setBackgroundInfo] = useState<string>('');
  const [messageLimit, setMessageLimit] = useState<number>(30);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // The two calls are independent: if settings fail to load, the account list is
    // still useful, and vice versa. Loading them together previously meant one
    // failure hid the other and showed a misleading "connect Facebook" message.
    const describe = (err: any, label: string) => {
      const data = err?.response?.data;
      const detail = data?.message || data?.error || err?.message || '';
      return `${label}${detail ? `: ${detail}` : ''}`;
    };

    const fetchData = async () => {
      const problems: string[] = [];

      try {
        const { data: current } = await getMetaMessengerIntegration();
        setIntegration(current);
        setSelectedPageId(current.page?.id ?? null);
        setSelectedInstagramId(current.instagram?.id ?? null);
        setBackgroundInfo(current.backgroundInfo ?? '');
        setMessageLimit(current.messageLimit ?? 30);
      } catch (err) {
        problems.push(describe(err, 'Could not load your saved settings'));
      }

      try {
        const { data: accounts } = await getMetaMessengerAccounts();
        setPages(accounts.pages ?? []);
        setInstagramAccounts(accounts.instagramAccounts ?? []);
      } catch (err) {
        problems.push(describe(err, 'Could not load your Meta accounts'));
      }

      if (problems.length > 0) {
        setError(problems.join(' · '));
      }
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    setError('');
    if (!selectedPageId) {
      setError('Select a Facebook page to continue.');
      return;
    }
    if (!backgroundInfo.trim()) {
      setError('Background information is required.');
      return;
    }
    if (!messageLimit || messageLimit < 1) {
      setError('Message limit must be 1 or more.');
      return;
    }

    setIsSaving(true);
    try {
      const isAccountChanged =
        selectedPageId !== integration?.page?.id ||
        selectedInstagramId !== (integration?.instagram?.id ?? null);

      if (isAccountChanged) {
        await connectMetaMessenger(selectedPageId, selectedInstagramId);
      }
      await updateMetaMessengerSettings(backgroundInfo.trim(), messageLimit);
      onSaved('Meta Messenger settings saved successfully!');
    } catch (err: any) {
      const responseData = err.response?.data;
      setError(
        responseData?.message ||
          responseData?.error ||
          err.message ||
          'Something went wrong. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectPage = (pageId: string) => {
    setSelectedPageId(selectedPageId === pageId ? null : pageId);
  };

  const handleSelectInstagram = (accountId: string) => {
    setSelectedInstagramId(
      selectedInstagramId === accountId ? null : accountId
    );
  };

  return (
    <div className="d-flex-center w-100 h-100vh p-absolute top-0 left-0">
      <div className="d-flex-start-start w-30rem max-h-80vh z4 bg-white b-radius-0_5rem">
        <div className="d-flex-row-space-between w-100 p-1rem bb-1-l-gray">
          <span className="ff-poppins fw-500 fs-0_875rem c-regular">
            Meta Messenger
          </span>
          <div className="d-flex-center icon-hovered" onClick={onClose}>
            <CloseIcon />
          </div>
        </div>

        <div className="d-flex-start-start w-100 overflow-auto">
          {isLoading ? (
            <div className="d-flex-center w-100 p-1rem">
              <span className="ff-inter c-111928 fs-0_875rem">Loading...</span>
            </div>
          ) : (
            <div className="d-flex-start-start w-100 p-1rem">
              <span className="ff-inter c-6b7280 fs-0_75rem mb-1rem">
                Connect the Facebook page whose Messenger inbox the AI should
                answer. Instagram direct messages are handled through the same
                page.
              </span>

              <label className="campaign-label-input">Facebook page</label>
              <div className="d-flex-start-start w-100 mb-1rem">
                {pages.length > 0 ? (
                  pages.map((page) => (
                    <div
                      className="d-flex-row w-100 p-0_5rem bb-1 hovered"
                      key={page.id}
                      onClick={() => handleSelectPage(page.id)}
                    >
                      <div className="d-flex-center mr-1rem">
                        <input
                          type="checkbox"
                          className="conversation-filter-checkbox"
                          checked={selectedPageId === page.id}
                          readOnly
                        />
                      </div>
                      <span className="ff-inter c-111928 fs-0_875rem">
                        {page.name}
                      </span>
                    </div>
                  ))
                ) : (
                  <span className="ff-inter c-6b7280 fs-0_875rem">
                    No Facebook pages found. Connect Facebook first.
                  </span>
                )}
              </div>

              {selectedPageId && instagramAccounts.length > 0 && (
                <>
                  <label className="campaign-label-input">
                    Instagram account (optional)
                  </label>
                  <div className="d-flex-start-start w-100 mb-1rem">
                    {instagramAccounts.map((account) => (
                      <div
                        className="d-flex-row w-100 p-0_5rem bb-1 hovered"
                        key={account.id}
                        onClick={() => handleSelectInstagram(account.id)}
                      >
                        <div className="d-flex-center mr-1rem">
                          <input
                            type="checkbox"
                            className="conversation-filter-checkbox"
                            checked={selectedInstagramId === account.id}
                            readOnly
                          />
                        </div>
                        <span className="ff-inter c-111928 fs-0_875rem">
                          {account.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <label className="campaign-label-input">
                Background Information
              </label>
              <textarea
                className="campaign-textarea w-100 mb-1rem"
                placeholder="What should the AI know when replying? Your product, offers, tone, anything it should mention."
                maxLength={20000}
                value={backgroundInfo}
                onChange={(e) => setBackgroundInfo(e.target.value)}
              />

              <label className="campaign-label-input">
                Message Limit Count
              </label>
              <input
                className="campaign-input w-100"
                type="number"
                min={1}
                value={messageLimit}
                onChange={(e) => setMessageLimit(Number(e.target.value))}
              />
              <span className="ff-inter c-6b7280 fs-0_75rem mt-0_5rem">
                How many replies the AI sends in one conversation before handing
                over to your team.
              </span>

              {error && <div className="form-error mt-0_5rem">{error}</div>}
            </div>
          )}
        </div>

        <div className="d-flex-row-space-between w-100 p-1rem">
          <button
            className="btn btn-cancel w-10rem h-2_375rem"
            onClick={onClose}
          >
            <span className="ff-poppins c-black fs-0_75rem fw-600">Cancel</span>
          </button>
          <button
            className="btn btn-primary w-17rem h-2_375rem"
            disabled={isLoading || isSaving}
            onClick={handleSave}
          >
            <span className="ff-poppins c-white fs-0_75rem fw-600">
              {isSaving ? 'Saving...' : 'Save'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetaMessengerModal;
